/**
 * Web Speech API — speech recognition (dictation) helpers for chat.
 */

export function isSpeechRecognitionSupported() {
  if (typeof window === 'undefined') return false;
  return Boolean(window.SpeechRecognition || window.webkitSpeechRecognition);
}

export function createSpeechRecognition({
  lang = 'en-US',
  continuous = false,
  interimResults = true,
} = {}) {
  const Recognition = window.SpeechRecognition || window.webkitSpeechRecognition;
  if (!Recognition) return null;

  const recognition = new Recognition();
  recognition.lang = lang;
  recognition.continuous = continuous;
  recognition.interimResults = interimResults;
  recognition.maxAlternatives = 1;
  return recognition;
}

/**
 * Reliably unlock / verify mic access before SpeechRecognition.
 * Many browsers report SpeechRecognition "not-allowed" unless getUserMedia
 * has already granted the mic for this origin in the current session.
 */
export async function ensureMicrophoneAccess() {
  if (typeof window === 'undefined') {
    return { ok: false, reason: 'unsupported' };
  }

  if (!window.isSecureContext) {
    return { ok: false, reason: 'insecure' };
  }

  if (!navigator.mediaDevices?.getUserMedia) {
    return { ok: false, reason: 'unsupported' };
  }

  // Prefer Permissions API when available (not supported for mic on all browsers)
  try {
    if (navigator.permissions?.query) {
      const status = await navigator.permissions.query({ name: 'microphone' });
      if (status.state === 'denied') {
        return { ok: false, reason: 'denied' };
      }
    }
  } catch {
    /* Safari / some browsers throw — ignore and try getUserMedia */
  }

  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
      },
      video: false,
    });
    // Immediately release hardware — we only needed the permission grant
    stream.getTracks().forEach((track) => {
      try {
        track.stop();
      } catch {
        /* ignore */
      }
    });
    return { ok: true, reason: 'granted' };
  } catch (err) {
    const name = String(err?.name || '');
    if (name === 'NotAllowedError' || name === 'PermissionDeniedError' || name === 'SecurityError') {
      return { ok: false, reason: 'denied' };
    }
    if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
      return { ok: false, reason: 'no-device' };
    }
    if (name === 'NotReadableError' || name === 'TrackStartError') {
      return { ok: false, reason: 'busy' };
    }
    return { ok: false, reason: 'failed' };
  }
}

export function micAccessErrorMessage(reason) {
  switch (reason) {
    case 'denied':
      return 'Microphone is blocked for this site. Click the lock/tune icon in the address bar → Site settings → Microphone → Allow, then reload.';
    case 'insecure':
      return 'Voice input needs a secure (https) page.';
    case 'no-device':
      return 'No microphone was found. Plug in a mic or check Windows sound settings.';
    case 'busy':
      return 'Microphone is in use by another app. Close it and try again.';
    case 'unsupported':
      return 'Voice input is not supported in this browser. Try Chrome or Edge.';
    default:
      return 'Could not access the microphone. Reload the page and try again.';
  }
}
